/** Report detail page, holding tabs for analysis, kinematics, and overview */
import { IonContent, IonGrid, IonPage } from '@ionic/react';
import { Suspense, useEffect, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router';

import { OverviewReportTab } from '@/pages/ReportV2/OverviewReportTab';
import { SingleLegSquatAnalysisReportTab } from '@/pages/ReportV2/SingleLegSquatsReportV2/SingleLegSquatsAnalysisReportTab';
import { SingleLegSquatsKinematicsReportTab } from '@/pages/ReportV2/SingleLegSquatsReportV2/SingleLegSquatsKinematicsReportTab';
import { SingleLegSquatsVariabilityReportTab } from '@/pages/ReportV2/SingleLegSquatsReportV2/SingleLegSquatsVariabilityReportTab';
// Styles
import styles from '@/pages/ReportV2/Styles/ReportDetailPage.module.css';

import { FloatingButton } from '@/components/FloatingButton/FloatingButton';
import { Loading } from '@/components/Loading/Loading';
import { PageHeader } from '@/components/PageHeader/PageHeader';
import { PageTopBar } from '@/components/PageTopBar/PageTopBar';
import { SegmentButton, SegmentedControl } from '@/components/SegmentedControl';
import { toast } from '@/components/Toaster/Toast';

import { getTitleAndDescriptionFromReportData } from '@/utils/getTitleAndDescriptionFromReportData';
import { mergeClassNames } from '@/utils/mergeClassNames';

import { createSingleLegSquatReportData } from '@/hooks/useReports';

import { useReportContext } from '@/contexts/reportContext';

import { ReportInfoId } from '@/models';
import { SingleLegSquatReportData } from '@/models/reportV2.model';

interface ParamTypes {
  id: ReportInfoId;
}
/** Report detail page
 * Loading the report data for the given userId and reportInfoId in the url.
 * Switch the tabs from Walk to Run depending on reportType in the loaded report data.
 * @param reportInfoId - the reportInfoId from the url
 */
export const SingleLegSquatReportDetailPageV2 = () => {
  const location = useLocation<{ title: string; description: string; backTarget?: string | null }>();
  const { id } = useParams<ParamTypes>();
  const { getReportData, dataReady, data, setDataReady, resetData } = useReportContext();
  const [loading, setLoading] = useState(true);
  const [reportData, setSLSquatReportData] = useState<SingleLegSquatReportData | undefined>();
  const [activeTab, setActiveTab] = useState('analysis');
  const [floatingButtons, setFloatingButtons] = useState<React.ReactNode>(null);

  const { title, description, backTarget } = location.state || getTitleAndDescriptionFromReportData(reportData);
  const showOverviewTab = !backTarget;

  const history = useHistory();
  // Fetch the report data

  useEffect(() => {
    getReportData(id);
    // Cleanup the data when the component unmounts
    return () => {
      setLoading(true);
      setSLSquatReportData(undefined);
      setDataReady(false); // Reset dataReady in the context
      resetData();
    };
  }, [id]);

  useEffect(() => {
    if (data) {
      setSLSquatReportData(createSingleLegSquatReportData(data));

      toast({
        icon: 'info',
        title: 'Enlarge graphs',
        description: 'Do this by clicking / tapping on the chart',
        duration: 3000,
      });
    } else {
      setSLSquatReportData(undefined);
    }
  }, [dataReady]);

  useEffect(() => {
    if (reportData !== undefined && data) setLoading(false);
  }, [reportData]);

  useEffect(() => {
    if (import.meta.env.MODE !== 'production') console.log('reportData', reportData);
  }, [reportData]);

  const handleOnSegmentClick = (target: string) => {
    setActiveTab(target);
    history.push(`/reportsV2/SingleLegSquat/${id}/${target}`);
  };

  const segments: SegmentButton[] = [
    { title: 'Analysis', value: 'analysis', onClick: () => handleOnSegmentClick('analysis') },
    { title: 'Variability', value: 'variability', onClick: () => handleOnSegmentClick('variability') },
    { title: 'Kinematics', value: 'kinematics', onClick: () => handleOnSegmentClick('kinematics') },
    ...(showOverviewTab
      ? [{ title: 'Overview', value: 'overview', onClick: () => handleOnSegmentClick('overview') }]
      : []),
  ];

  return (
    <IonPage>
      <PageHeader
        isLoading={loading}
        topSlot={
          <PageTopBar
            backTarget={backTarget ? backTarget : '/reports'}
            description={description}
            title={title}
            variant='simple'
          />
        }>
        <SegmentedControl segments={segments} value={activeTab} variant='default' />
      </PageHeader>
      <div className={styles.background}>
        <IonContent className={mergeClassNames('ion-padding', styles.content)} color='light' id='main'>
          <IonGrid fixed style={{ height: '100%' }}>
            {loading && <Loading />}

            {reportData && (
              <>
                <Suspense fallback={<Loading />}>
                  {activeTab === 'analysis' && (
                    <SingleLegSquatAnalysisReportTab tabData={reportData.squatAnalysisData} />
                  )}
                  {activeTab === 'variability' && (
                    <SingleLegSquatsVariabilityReportTab tabData={reportData.squatVariabilityData} />
                  )}
                  {activeTab === 'kinematics' && (
                    <SingleLegSquatsKinematicsReportTab
                      setFloatingButtons={setFloatingButtons}
                      tabData={reportData.squatKinematicData}
                    />
                  )}
                  {showOverviewTab && activeTab === 'overview' && (
                    <OverviewReportTab fileInfo={reportData.fileInfo} id={id} reportType={reportData.reportType} />
                  )}
                </Suspense>
              </>
            )}
          </IonGrid>
        </IonContent>
        {floatingButtons && (
          <FloatingButton horizontal='end' side='top' slot='fixed' vertical='bottom'>
            {floatingButtons}
          </FloatingButton>
        )}
      </div>
    </IonPage>
  );
};
